import clsx from 'clsx';
import { useEffect, useState, useRef } from 'react';
import { useSession } from '@marriott/mi-headless-utils';
import { Heading, headingType, size, Image, Button } from '@marriott/mi-ui-library';
import { MerchandisingSmallBannerV3Props } from '../organisms/MerchandisingSmallBanner/MerchandisingSmallBanner.types';
import { removeParentTagRTE, formatImageUrl, getTrackingPropertiesDynamic } from '../utils/helpers';
import { MarkDownText } from './markDown/MarkDown';
import DOMPurify from 'isomorphic-dompurify';

export const MerchandisingSmallBannerV3 = (props: MerchandisingSmallBannerV3Props) => {
  const {
    isTargetContentLoaded,
    isTargetContentError = false,
    colorScheme = 'color-scheme1',
    headerMedium,
    descriptionMedium,
    tertiaryImage,
    buttonProps,
    logo,
    image,
  } = props;

  const ref = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;
  const { datalayer } = useSession();
  const trackingPropertiesDynamic = getTrackingPropertiesDynamic(
    buttonProps?.trackingProperties,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buttonProps?.metaData as unknown as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buttonProps?.targetData as unknown as any,
    datalayer?.['mr_prof_authentication_state'] === 'authenticated',
    isTargetContentError,
    buttonProps?.mboxParameter
  );
  const [descriptionHTML, setDescriptionHTML] = useState<string>('');

  useEffect(() => {
    if (isTargetContentLoaded) {
      setDescriptionHTML((DOMPurify.sanitize(descriptionMedium, { RETURN_DOM: true }) as HTMLElement)?.innerHTML);
    }
  }, [isTargetContentLoaded, descriptionMedium]);

  const headingProps = {
    class: clsx('merchandising-small-bannerv3__heading mb-md-1 text-center text-md-left'),
    content: headerMedium?.length > 0 ? removeParentTagRTE(headerMedium) : '',
  };

  return (
    <div
      className={clsx(
        `merchandising-small-bannerv3 t-background-color ${colorScheme} d-flex align-items-center flex-column flex-md-row px-4 pt-0`
      )}
    >
      <div
        className={clsx(
          'merchandising-small-bannerv3__image-container order-3 order-md-1 mt-4 mt-md-0',
          !isTargetContentLoaded && 'skeleton-loader-composite loading'
        )}
      >
        {isTargetContentLoaded && (
          <Image
            customClass={clsx('merchandising-small-bannerv3__image')}
            altText={logo ? logo?.altText : image?.altText}
            defaultImageURL={logo?.imageSrc ?? image?.assetPath}
          />
        )}
      </div>

      <div
        className={clsx(
          'merchandising-small-bannerv3__content d-flex flex-column justify-content-between align-items-center flex-grow-1 pl-md-5 mt-md-0 flex-md-row order-1 order-md-2',
          !isTargetContentLoaded && 'loading'
        )}
      >
        <div
          className={clsx(
            'merchandising-small-bannerv3__textContent my-md-5 py-md-2 py-lg-0 d-flex flex-column justify-content-center align-items-center align-items-md-start',
            !descriptionMedium ? 'my-lg-5' : 'my-lg-4',
            !isTargetContentLoaded && 'loading'
          )}
        >
          <div
            className={clsx(
              'bvy-color-scheme1',
              !isTargetContentLoaded && 'merchandising-small-bannerv3__heading skeleton-loader-composite loading py-2'
            )}
          >
            {isTargetContentLoaded && (
              <MarkDownText
                textContent={headingProps.content}
                className={headingProps.class}
                variation={headingType.title}
                fontSize={size.small}
                fallback={
                  <Heading
                    customClass={headingProps.class}
                    variation={headingType.title}
                    fontSize={size.small}
                    // Using removeParentTagRTE to clean the text recieved from RTE
                    dangerouslySet={headingProps.content}
                  />
                }
              />
            )}
          </div>
          {descriptionMedium && (
            <div
              className={clsx(
                'merchandising-small-bannerv3__description t-subtitle-m mt-2 mb-3 my-md-0 text-center text-md-left',
                !isTargetContentLoaded && 'skeleton-loader-composite loading py-2'
              )}
              dangerouslySetInnerHTML={{ __html: descriptionHTML }}
            />
          )}
        </div>
        <div
          className={clsx(
            'd-flex flex-column-reverse justify-content-between align-items-md-end align-items-lg-center my-md-4 my-lg-0 flex-lg-row justify-lg-center'
          )}
        >
          {buttonProps?.callToActionText1 && (
            <div
              className={clsx(
                'ml-md-1 mt-lg-0',
                tertiaryImage?.imageSrc ? 'mt-md-3' : '',
                !descriptionMedium ? 'mt-3 mt-md-0' : '',
                !isTargetContentLoaded && 'merchandising-small-bannerv3__button skeleton-loader-composite loading py-3'
              )}
            >
              {isTargetContentLoaded && (
                <Button
                  className={clsx('merchandising-small-banner__button', `m-button-s text-center align-self-end pt-2`)}
                  href={buttonProps?.destinationUrl1}
                  isLink={true}
                  target={buttonProps?.openInNewTab === true ? '_blank' : '_self'}
                  trackingProperties={trackingPropertiesDynamic}
                  rel={`${buttonProps?.openInNewTab ? 'noopener noreferrer' : ''}`}
                  ref={ref}
                  buttonCopy={buttonProps?.callToActionText1}
                  linkAriaLabelOpenNewWindow={buttonProps?.openInNewWindowAriaLabel}
                  compName={buttonProps?.trackingProperties?.trackingContentPosition}
                  data-is-click-track={buttonProps?.trackingProperties?.clickTrack}
                />
              )}
            </div>
          )}

          {tertiaryImage?.imageSrc && (
            <div
              className={clsx(
                'merchandising-small-bannerv3__qr order-2 order-lg-3 d-none d-md-block pl-md-4',
                !isTargetContentLoaded && 'merchandising-small-bannerv3__qr-image skeleton-loader-composite loading'
              )}
            >
              {isTargetContentLoaded && (
                <Image
                  customClass={'merchandising-small-bannerv3__qr-image'}
                  altText={tertiaryImage?.altText}
                  defaultImageURL={formatImageUrl(tertiaryImage?.imageSrc)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
